import React, {
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import clsx from 'clsx';
import omit from 'lodash/omit';
import cloneDeep from 'lodash/cloneDeep';
import uniqueId from 'lodash/uniqueId';
import compact from 'lodash/compact';
import forIn from 'lodash/forIn';
import keyBy from 'lodash/keyBy';
import values from 'lodash/values';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import {
  DragDropContext,
  DropResult,
} from '@hello-pangea/dnd';
import {
  FormProvider,
  useForm,
} from 'react-hook-form';
import {
  SplitClaimsDTO,
} from 'dtos';
import {
  reorder,
} from 'utils/array';
import {
  formatDate,
} from 'utils/date';
import {
  useNotify,
} from 'core/hooks';
import {
  claimEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  PatientForm,
} from 'pages/Dashboard/types/patient';
import Dialog from 'pages/Dashboard/components/Dialog';
import Button from 'pages/Dashboard/components/Button';
import FieldsArrayWrapper from 'pages/Dashboard/pages/Charts/pages/Details/components/FieldsArrayWrapper';
import {
  Claim,
  ClaimSplit,
} from 'pages/Dashboard/pages/Billing/types';
import SplitItem from 'pages/Dashboard/pages/Billing/ClaimForm/SplitItem';
import {
  useGetClaimSplitsByEncounter,
  useSaveSplitClaim,
} from 'pages/Dashboard/pages/Billing/hooks';
import Spinner from 'pages/Dashboard/components/Spinner';
import {
  serializeClaimSplits,
} from 'pages/Dashboard/pages/Billing/helpers';

type Props = {
  claim: Claim;
  onClose: () => void;
};

type ClaimSplitsForm = { splitClaims: ClaimSplit[] | null };

export const getUniqueSplitId = () => uniqueId('new-claim__');

const getDefaultValue = (): ClaimSplit => ({
  externalId: '',
  claimId: getUniqueSplitId(),
  serviceCodes: [],
  insuranceId: 0,
  status: 'Draft',
});

type TitleProps = Pick<SplitClaimsDTO, 'visitNote'> & {
  isLoading: boolean;
};

function Title({
  visitNote,
  isLoading,
}: TitleProps) {
  return (
    <div className="flex flex-col w-full gap-px">
      <div className="flex items-center gap-2 w-full font-semibold ">
        <h2 className="text-base">
          Claim Split
          {isLoading ? '' : ' for '}
        </h2>
        {!isLoading && (
          <>
            <span className="text-blue-500">
              {visitNote?.reasonForVisit}
            </span>
            <span className="font-semibold">
              {'on '}
              {formatDate(visitNote?.visitDateTime ?? '')}
            </span>
          </>
        )}
      </div>
    </div>
  );
}

export default function ClaimSplitDialog({
  claim,
  onClose,
}: Props) {
  const form = useForm<ClaimSplitsForm>();
  const notify = useNotify();
  const {
    reset,
    handleSubmit,
    setValue,
    watch,
    formState: { isSubmitting },
  } = form;
  const { data, isFetching: isLoading } = useGetClaimSplitsByEncounter(claim?.encounterId);
  const { mutateAsync: saveSplit } = useSaveSplitClaim();
  const formValue = watch('splitClaims');

  const splits = keyBy(formValue, 'claimId');
  const allServiceCodes = useMemo(() => compact(map(splits, 'serviceCodes').flat()), [splits]);
  const keyedServiceCodes = keyBy(allServiceCodes, 'visitNoteBillingCodeId');

  const { visitNote, patient } = data ?? {};

  useEffect(() => {
    if (!isLoading && !isEmpty(data)) {
      reset({ splitClaims: data?.splitClaims ?? [] });
    }
  }, [data, isLoading]);

  const onSubmit = async (formData: ClaimSplitsForm) => {
    try {
      await saveSplit({
        ...omit(data, 'splitClaims'),
        ...serializeClaimSplits(formData?.splitClaims ?? []),
      });
      claimEventChannel.emit('onClaimSplit', claim);
      notify({
        message: 'Claim splits were saved.',
        variant: 'success',
      });
      onClose();
    } catch (e: unknown) {
      console.error(e);
    }
  };

  const onDragEnd = useCallback((result: DropResult) => {
    if (typeof result.destination !== 'undefined') {
      const {
        destination,
        source: { droppableId: sourceClaimId },
        draggableId: droppedCode,
      } = result;

      const { droppableId: targetClaimId = '', index: newIndex = 0 } = destination ?? {};

      const newSplits: Record<string, ClaimSplit> = {};

      if (sourceClaimId !== targetClaimId) {
        const clonedSplits = cloneDeep(splits);

        if (!(targetClaimId in clonedSplits)) {
          clonedSplits[targetClaimId] = { claimId: targetClaimId?.toString() };
        }

        forIn(clonedSplits, (split, claimId) => {
          if (!(claimId in newSplits)) {
            newSplits[claimId] = {
              externalId: '',
              status: 'Draft',
              insuranceId: 0,
              ...splits[claimId],
              claimId: claimId.toString(),
              serviceCodes: split.serviceCodes ?? [],
            };
          }
        });
        newSplits[targetClaimId]?.serviceCodes?.push({
          ...keyedServiceCodes[droppedCode],
          serviceCodeDetails: {
            ...(keyedServiceCodes[droppedCode]?.serviceCodeDetails ?? {}),
            primaryClaimId: targetClaimId?.toString() ?? '',
          },
        });

        const previousCodeIndex = newSplits[sourceClaimId]?.serviceCodes
          ?.findIndex(({ visitNoteBillingCodeId }) => (
            visitNoteBillingCodeId?.toString() === droppedCode
          )) ?? 0;

        newSplits[sourceClaimId].serviceCodes = [
          ...(newSplits[sourceClaimId].serviceCodes ?? []).slice(0, previousCodeIndex),
          ...(newSplits[sourceClaimId].serviceCodes ?? []).slice(previousCodeIndex + 1),
        ];
      }
      const _splits = sourceClaimId !== targetClaimId
        ? newSplits
        : splits;
      const droppedIndex = (_splits[targetClaimId]?.serviceCodes ?? [])
        ?.findIndex((x) => x.visitNoteBillingCodeId?.toString() === droppedCode) ?? 0;

      setValue('splitClaims', values({
        ..._splits,
        [targetClaimId]: {
          ..._splits[targetClaimId],
          serviceCodes: reorder(_splits[targetClaimId]?.serviceCodes ?? [], droppedIndex, newIndex),
        },
      }));
    }
  }, [splits]);

  return (
    <Dialog
      open
      onClose={onClose}
      title={(
        <Title
          visitNote={visitNote}
          isLoading={isLoading}
        />
      )}
      className="!w-[960px] !overflow-[initial]"
    >
      <FormProvider {...form}>
        <div className="flex flex-col justify-between gap-4 w-full overflow-y-hidden">
          <div className={clsx('flex flex-1 w-full', {
            'items-center justify-center': isLoading,
          })}
          >
            {isLoading && (
              <div className="flex items-center gap-2 min-h-[320px]">
                <Spinner size={24} />
                Loading...
              </div>
            )}
            {!isLoading && (
              <DragDropContext onDragEnd={onDragEnd}>
                <FieldsArrayWrapper
                  classes={{
                    root: 'flex flex-row flex-wrap h-full min-h-[320px] flex-1 w-full gap-3 overflow-y-auto',
                    field: 'flex flex-wrap items-start justify-start flex-row !w-full gap-3 !space-y-0',
                  }}
                  formKey="splitClaims"
                  FieldComponent={SplitItem}
                  getDefaultValue={getDefaultValue}
                  patient={patient as PatientForm}
                  append={null}
                />
              </DragDropContext>
            )}
          </div>
          <div className="flex gap-2 mb-2 justify-end w-full">
            <Button
              variant="outlined"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Close
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit(onSubmit)}
              disabled={isSubmitting}
            >
              Confirm
            </Button>
          </div>
        </div>
      </FormProvider>
    </Dialog>
  );
}
